import { useCallback, useEffect, useRef, useState } from 'react';
import { HtmlRenderer } from '@amzn/stencil-react-components/rte';

import { useMessages } from '../i18n/useMessages';
import { MediaPreviewModal } from './MediaPreviewModal';

/** Debounce window for image error callbacks (ms). Multiple broken
 *  images (common when a batch of presigned URLs expires at once)
 *  coalesce into a single invalidation round rather than hammering
 *  React Query with N concurrent refetches. */
const IMAGE_ERROR_DEBOUNCE_MS = 500;

/**
 * Maximum number of recovery attempts per HTML content change.
 * After this many failed rounds, the component stops retrying and
 * lets the browser show its broken-image placeholder — the image is
 * genuinely missing (404), not just an expired presigned URL.
 */
const MAX_IMAGE_ERROR_RECOVERIES = 3;

const BUNDLE = 'shared.ui.translations.richHtmlContent';
type BundleKey = 'previewUnsupported' | 'downloadLabel';

interface RichHtmlContentProps {
  /** HTML string to render. Must be from a trusted source (internal API). */
  html: string;
  /**
   * Called (debounced) when an `<img>` inside the rendered HTML fires
   * an `error` event — typically because a presigned URL expired.
   * Wire this to a React Query invalidation so the parent refetches
   * and the component re-renders with fresh URLs.
   *
   * Optional — when omitted, image errors are silently ignored (the
   * browser shows its broken-image placeholder as before).
   */
  onImageError?: (() => void) | undefined;
  /**
   * When true, tapping an embedded `<img>` opens it in a full-screen
   * `MediaPreviewModal`. Enabled by default — pass false to suppress
   * the behavior in contexts where inline image clicks should not
   * trigger a modal (e.g. print views).
   */
  enableImagePreview?: boolean | undefined;
}

/**
 * Renders trusted HTML content using the Stencil design-system
 * `HtmlRenderer` component — applies proper typography tokens,
 * spacing, and responsive image constraints out of the box.
 *
 * Adds a capture-phase `error` event listener on the container so
 * that broken `<img>` elements (expired presigned URLs on AEA
 * WebView) trigger `onImageError`, which callers wire to query
 * invalidation for self-healing recovery.
 *
 * Adds a capture-phase `click` listener that opens a full-screen
 * `MediaPreviewModal` when the user taps an inline `<img>`. This
 * gives all consumers (TaskDetailPage, PerformAuditQuestionCard,
 * QuestionInfoExpander, PerformAuditSubQuestionRow) tap-to-preview
 * without any wiring changes.
 *
 * Drop-in replacement for `RichContent` in V2 perform-audit and
 * task-detail consumers. `RichContent` remains available for other
 * consumers that don't need the image-error recovery.
 */
export function RichHtmlContent({
  html,
  onImageError,
  enableImagePreview = true,
}: RichHtmlContentProps): JSX.Element {
  const { t } = useMessages<BundleKey>(BUNDLE);
  const timerRef = useRef<number | null>(null);
  const recoveryCountRef = useRef(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Reset recovery counter when new HTML arrives (fresh URLs from a
  // successful refetch). This allows the next batch of errors to
  // trigger recovery again — only permanently broken images exhaust
  // the cap within a single HTML snapshot.
  useEffect(() => {
    recoveryCountRef.current = 0;
  }, [html]);

  // Cancel any pending debounce timer on unmount so a late-firing
  // callback doesn't call onImageError against stale state.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Capture-phase handler — `error` events on <img> do NOT bubble,
  // so we must listen during the capture phase to intercept them on
  // the container div.
  const handleErrorCapture = useCallback(
    (event: React.SyntheticEvent<HTMLDivElement>): void => {
      if (!onImageError) return;
      const target = event.target as HTMLElement;
      if (target.tagName !== 'IMG') return;
      if (recoveryCountRef.current >= MAX_IMAGE_ERROR_RECOVERIES) return;

      // Debounce: a single expired S3 token often breaks 3-5 images
      // simultaneously. One invalidation round is enough.
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        recoveryCountRef.current += 1;
        onImageError();
      }, IMAGE_ERROR_DEBOUNCE_MS);
    },
    [onImageError],
  );

  // Capture-phase click handler — opens the image preview modal when
  // the user taps an <img> element rendered inside the HTML content.
  const handleClickCapture = useCallback(
    (event: React.SyntheticEvent<HTMLDivElement>): void => {
      if (!enableImagePreview) return;
      const target = event.target as HTMLElement;
      if (target.tagName !== 'IMG') return;
      const src = (target as HTMLImageElement).src;
      if (src) {
        setPreviewUrl(src);
      }
    },
    [enableImagePreview],
  );

  const handleClosePreview = useCallback((): void => {
    setPreviewUrl(null);
  }, []);

  return (
    <>
      <div
        onErrorCapture={handleErrorCapture}
        onClickCapture={handleClickCapture}
        className="rich-html-content-compact"
      >
        <HtmlRenderer html={html} />
      </div>

      {previewUrl && (
        <MediaPreviewModal
          isOpen
          fileName={deriveFileName(previewUrl)}
          url={previewUrl}
          unsupportedText={t('previewUnsupported')}
          downloadLabel={t('downloadLabel')}
          onClose={handleClosePreview}
        />
      )}
    </>
  );
}

/** Image extensions recognized by getMediaPreviewKind. */
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp']);

/**
 * Derives a display-friendly file name from a presigned URL, ensuring
 * the result always carries a recognized image extension so that
 * `MediaPreviewModal` renders the `<img>` preview (not the "unsupported"
 * fallback).
 *
 * Presigned S3 URLs in this app typically encode the media-id (a UUID)
 * as the final path segment — e.g. `.../abc-123?X-Amz-...` — with no
 * file extension. Since the click handler only fires on `<img>` elements
 * that already rendered successfully, we know the URL points to valid
 * image data and can safely default to `.png`.
 */
function deriveFileName(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split('/').pop() ?? '';
    const decoded = decodeURIComponent(lastSegment) || 'image';
    // If the extracted name already has a recognized image extension,
    // return it as-is (e.g. "photo.jpg"). Otherwise append .png so
    // getMediaPreviewKind returns IMAGE.
    const ext = decoded.split('.').pop()?.toLowerCase() ?? '';
    if (IMAGE_EXTENSIONS.has(ext)) return decoded;
    return `${decoded}.png`;
  } catch {
    return 'image.png';
  }
}
